import { EMPTY_DISTRIBUTION } from './distributionInheritance';

// The TO's values entered on a room or a room × season (KD-3896).
const distributionOf = (distributions, toOrganization) => {
  const entry = distributions?.find(
    (item) => item?.organization?.id === toOrganization?.id,
  );
  return entry
    ? {
        margin: entry.margin,
        isMarginPercent: entry.isMarginPercent,
        allotmentCount: entry.allotmentCount,
        freeSale: entry.freeSale,
      }
    : { ...EMPTY_DISTRIBUTION };
};

/**
 * `periods` and `savedRooms` are the unsaved values of the hotel (its
 * `periods` and `rooms` payloads); they win over the stored ones.
 */
export const getRoomsDetail = (
  data,
  periods,
  dmcOrganization,
  toOrganization,
  savedRooms,
) => {
  const rooms = [];

  data?.data.forEach((contract, contractIndex) => {
    contract.rooms.forEach((room) => {
      let roomInArray = rooms.find((elem) => elem.id === room.id);

      if (!roomInArray) {
        roomInArray = {
          id: room.id,
          name: room.name,
          periods: [],
          enabled: true,
          distribution: distributionOf(room.distributions, toOrganization),
          seasons: [],
        };

        rooms.push(roomInArray);
      }

      (room.seasons ?? []).forEach((season) => {
        if (roomInArray.seasons.some((elem) => elem.id === season.id)) return;
        roomInArray.seasons.push({
          id: season.id,
          contractId: contract.id,
          contractIndex,
          seasonId: season.seasonId,
          name: season.name,
          category: season.category,
          daysOfWeek: season.daysOfWeek,
          position: season.position,
          offered: season.offered,
          distribution: distributionOf(season.distributions, toOrganization),
        });
      });

      const periodsWithAllotment = room.periods;

      periodsWithAllotment.forEach((period) => {
        let periodInArray = roomInArray.periods.find(
          (elem) => elem.id === period.id,
        );

        const periodOrgaWithAllotment = period.periodOrganizations.find(
          (item) => item?.organization?.id === toOrganization?.id,
        );

        if (!periodInArray) {
          periodInArray = {
            id: period.id,
            startAt: period.startAt,
            endAt: period.endAt,
            seasonRoomId: period.seasonRoomId ?? null,
            // The TO's values entered on the period, null when inherited.
            own: {
              margin: periodOrgaWithAllotment?.ownMargin ?? null,
              isMarginPercent:
                periodOrgaWithAllotment?.ownIsMarginPercent ?? null,
              allotmentCount:
                periodOrgaWithAllotment?.ownAllotmentCount ?? null,
              freeSale: periodOrgaWithAllotment?.ownFreeSale ?? null,
            },
            freeSale: period.freeSale,
            totalAllotmentsAvailable: period.totalAllotmentsAvailable,
            toElement: {
              isPeriodAllowedForBooking:
                periodOrgaWithAllotment?.isPeriodAllowedForBooking,
              totalAllotmentsAvailableForDistribution:
                period.totalAllotmentsAvailable -
                period.totalSharedAllotmentsAvailable -
                period.periodOrganizations
                  .filter(
                    (elem) =>
                      elem.organization.id !== dmcOrganization?.id &&
                      elem.organization.id !== toOrganization?.id,
                  )
                  .reduce((acc, value) => acc + value.allotmentCount, 0),
            },
            allAllocations: period.periodOrganizations.filter(
              (elem) => elem.organization.id !== toOrganization?.id,
            ),
          };
          roomInArray.periods.push(periodInArray);
        }

        const periodInHotel = periods?.find((elem) => elem.id === period.id);

        if (periodInHotel) {
          const isPeriodAllowed =
            periodInHotel?.isPeriodAllowedForBooking ?? false;

          periodInArray.toElement = {
            ...periodInArray.toElement,
            allotmentCount: periodInHotel.allotmentCount,
            freeSale: periodInHotel.freeSale,
            isMarginPercent: periodInHotel?.isMarginPercent,
            margin: periodInHotel?.margin,
            isPeriodAllowedForBooking: isPeriodAllowed,
          };
          periodInArray.own = {
            margin: periodInHotel.margin ?? null,
            isMarginPercent: periodInHotel.isMarginPercent ?? null,
            allotmentCount: periodInHotel.allotmentCount ?? null,
            freeSale: periodInHotel.freeSale ?? null,
          };
        } else {
          const periodOrgaTo = period.periodOrganizations.find(
            (elem) => elem.organization.id === toOrganization?.id,
          );

          if (!periodOrgaTo) {
            periodInArray.toElement = {
              ...periodInArray.toElement,
              allotmentCount: 0,
              freeSale: false,
              isPeriodAllowedForBooking: false,
            };
          } else {
            const isPeriodAllowed =
              periodOrgaTo.isPeriodAllowedForBooking ?? false;

            periodInArray.toElement = {
              ...periodInArray.toElement,
              allotmentCount: periodOrgaTo.allotmentCount,
              freeSale: periodOrgaTo.freeSale,
              isPeriodAllowedForBooking: isPeriodAllowed,
              isMarginPercent: periodOrgaTo?.isMarginPercent,
              margin: periodOrgaTo?.margin,
            };
          }
        }
      });
    });
  });

  // After processing all contracts and periods, a room is enabled if at least
  // one of its periods is allowed for booking.
  rooms.forEach((room) => {
    room.enabled = room.periods.some((period) =>
      Boolean(period.toElement?.isPeriodAllowedForBooking),
    );

    const saved = savedRooms?.find((elem) => elem.id === room.id);
    if (saved) {
      room.distribution = { ...room.distribution, ...pickValues(saved) };
      room.seasons.forEach((season) => {
        const savedSeason = saved.seasons?.find(
          (elem) => elem.id === season.id,
        );
        if (savedSeason) {
          season.distribution = {
            ...season.distribution,
            ...pickValues(savedSeason),
          };
        }
      });
    }
    // A room may list the seasons of several contracts, in contract order.
    room.seasons.sort(
      (a, b) => a.contractIndex - b.contractIndex || a.position - b.position,
    );
  });

  return rooms;
};

// The values a payload sets: an absent one keeps the stored value.
const pickValues = (values) =>
  Object.fromEntries(
    ['margin', 'isMarginPercent', 'allotmentCount', 'freeSale']
      .filter((key) => values[key] !== undefined)
      .map((key) => [key, values[key]]),
  );
